/**
 * SalesChart CSS Styles
 *
 * CSS-in-JS styles injected once on first component instantiation.
 * Scoped via .sm-sales-chart prefix.
 */
let stylesInjected = false;
const STYLES = `
/* Sales Chart Container */
.sm-sales-chart {
  width: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Stats Row */
.sm-sales-chart-stats-wrap {
  margin-bottom: 16px;
}

.sm-sales-chart-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.sm-sales-chart-stat {
  text-align: center;
  padding: 12px 8px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.sm-sales-chart-stat-value {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
}

.sm-sales-chart-stat-label {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 2px;
}

/* Property Type filter button's color swatch - mirrors the Summary view's
   stacked-bar segment colors (see PROPERTY_TYPE_COLORS), so the button
   doubles as that chart's legend. Sits inline inside a plain
   .sm-sales-chart-btn (not its own flex chip) alongside the label/%. */
.sm-sales-chart-type-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 5px;
  vertical-align: middle;
}

/* Filters Container */
.sm-sales-chart-filters {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

/* Filter Row */
.sm-sales-chart-filter-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.sm-sales-chart-filter-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: #6b7280;
  min-width: 70px;
}

/* Button Group */
.sm-sales-chart-btn-group {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.sm-sales-chart-btn {
  padding: 6px 12px;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;
}

.sm-sales-chart-btn:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.sm-sales-chart-btn.active {
  background: #2c5530;
  border-color: #2c5530;
  color: white;
}

.sm-sales-chart-btn.active:hover {
  background: #1e3d22;
}

/* Chart Mode Toggle */
.sm-sales-chart-mode-toggle {
  display: flex;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  overflow: hidden;
  margin-left: auto;
}

.sm-sales-chart-mode-btn {
  padding: 6px 14px;
  font-size: 0.8rem;
  font-weight: 500;
  border: none;
  background: white;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;
}

.sm-sales-chart-mode-btn:first-child {
  border-right: 1px solid #d1d5db;
}

.sm-sales-chart-mode-btn:hover {
  background: #f3f4f6;
}

.sm-sales-chart-mode-btn.active {
  background: #2c5530;
  color: white;
}

.sm-sales-chart-mode-btn:disabled {
  color: #9ca3af;
  cursor: not-allowed;
  background: #f9fafb;
}

.sm-sales-chart-mode-btn:disabled:hover {
  background: #f9fafb;
}

/* Chart Canvas Container */
.sm-sales-chart-canvas-container {
  position: relative;
  width: 100%;
  height: 350px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
}

.sm-sales-chart-canvas-container canvas {
  width: 100% !important;
  height: 100% !important;
}

/* Legend */
.sm-sales-chart-legend {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.sm-sales-chart-legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: #4b5563;
}

.sm-sales-chart-legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.sm-sales-chart-partial-note {
  text-align: center;
  font-size: 0.72rem;
  color: #9ca3af;
  margin-top: 4px;
}

.sm-sales-chart-legend-dot.sold {
  background: #22c55e;
}

.sm-sales-chart-legend-dot.for-sale {
  background: #3b82f6;
  clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
  border-radius: 0;
}

.sm-sales-chart-legend-dot.pending {
  background: #eab308;
  clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
  border-radius: 0;
}

.sm-sales-chart-legend-line {
  width: 20px;
  height: 2px;
  background: #93c5fd;
  border-style: dashed;
}

/* Loading State */
.sm-sales-chart-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 350px;
  color: #6b7280;
  font-size: 0.9rem;
}

.sm-sales-chart-loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid #e5e7eb;
  border-top-color: #2c5530;
  border-radius: 50%;
  animation: sm-sales-chart-spin 0.8s linear infinite;
  margin-right: 8px;
}

@keyframes sm-sales-chart-spin {
  to { transform: rotate(360deg); }
}

/* Empty State */
.sm-sales-chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 350px;
  color: #9ca3af;
  font-size: 0.9rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px dashed #e5e7eb;
}

/* Responsive */
@media (max-width: 640px) {
  .sm-sales-chart-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .sm-sales-chart-filter-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .sm-sales-chart-filter-label {
    min-width: auto;
    margin-bottom: 4px;
  }

  .sm-sales-chart-mode-toggle {
    margin-left: 0;
    margin-top: 8px;
    width: 100%;
  }

  .sm-sales-chart-mode-btn {
    flex: 1;
  }

  .sm-sales-chart-canvas-container {
    height: 280px;
  }
}
`;
export function injectStyles() {
    if (stylesInjected)
        return;
    const styleEl = document.createElement('style');
    styleEl.id = 'sm-sales-chart-styles';
    styleEl.textContent = STYLES;
    document.head.appendChild(styleEl);
    stylesInjected = true;
}
//# sourceMappingURL=styles.js.map